<template>
  <div class="onboarding-container">
    <div class="onboarding-content">
      <div v-if="currentStep === 1 || currentStep === 2" class="onboarding-header">
        <h1 class="onboarding-title">Welcome!</h1>
        <p class="onboarding-subtitle">Let's secure your account</p>
      </div>
      <div v-if="currentStep === 3" class="onboarding-header">
        <h1 class="onboarding-title">Excellent!</h1>
        <p class="onboarding-subtitle">One last step to go</p>
      </div>

      <!-- Шаг 1: Создание PIN-кода -->
      <div v-if="currentStep === 1" class="onboarding-step">
        <BaseCard variant="highlighted" class="pin-card">
          <div class="pin-instruction">
            <h2 class="pin-title">Create your PIN code</h2>
            <p class="pin-description">Enter a 4-digit PIN to secure your application</p>
          </div>
          

          <PinInput
            v-model="pin"
            :length="4"
            @complete="handlePinComplete"
          />


          <div v-if="pinError" class="error-message">
            {{ pinError }}
          </div>
        </BaseCard>
      </div>

      <!-- Шаг 2: Подтверждение PIN-кода -->
      <div v-if="currentStep === 2" class="onboarding-step">
        <BaseCard variant="highlighted" class="pin-card">
          <div class="pin-instruction">
            <h2 class="pin-title">Confirm your PIN</h2>
            <p class="pin-description">Please enter your PIN again to confirm</p>
          </div>

          <PinInput
            v-model="confirmPin"
            :length="4"
            @complete="handleConfirmPinComplete"
          />

          <div v-if="confirmPinError" class="error-message">
            {{ confirmPinError }}
          </div>
        </BaseCard>
      </div>

      <!-- Шаг 3: Ввод реферального кода -->
      <div v-if="currentStep === 3" class="onboarding-step">
        <BaseCard variant="highlighted" class="referral-card">
          <div class="referral-instruction">
            <h2 class="referral-title">Enter Referral Code</h2>
            <p class="referral-description">To access app, please, enter the invite (referral) code from trader referral page</p>
          </div>

          <BaseInput
            v-model="refCode"
            placeholder="Enter referral code"
            class="referral-input"
          />

          <div v-if="refCodeError" class="error-message">
            {{ refCodeError }}
          </div>

          <BaseButton
            variant="primary"
            size="lg"
            fullWidth
            :disabled="!refCode || isSubmitting"
            @click="handleContinue"
            class="continue-button"
          >
            {{ isSubmitting ? 'Processing...' : 'Continue' }}
          </BaseButton>
        </BaseCard>
      </div>

      <p v-if="currentStep < 3" class="onboarding-subtitle" style="font-size: var(--font-size-sm); text-align: center;">Your account will be automatically created and loaded when you open the application from this Telegram account.</p>

      <!-- Прогресс -->
      <div class="progress-section">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: `${(currentStep / 3) * 100}%` }"></div>
        </div>
        <span class="progress-text">{{ currentStep }} / 3</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import BaseCard from '../components/ui/BaseCard.vue'
import BaseInput from '../components/ui/BaseInput.vue'
import BaseButton from '../components/ui/BaseButton.vue'
import PinInput from '../components/ui/PinInput.vue'
import { onboardingUtils } from '../utils/onboarding.js'
import { useTelegramData } from '../composables/useTelegram.js'
import { apiService } from '../services/api.js'

export default {
  name: 'OnboardingFlow',
  components: {
    BaseCard,
    BaseInput,
    BaseButton,
    PinInput
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const { userData, isLoading } = useTelegramData()

    // Определяем начальный шаг из query параметра, если он есть
    const initialStep = route.query.step ? parseInt(route.query.step) : 1
    const currentStep = ref(initialStep >= 1 && initialStep <= 3 ? initialStep : 1)
    const pin = ref('')
    const confirmPin = ref('')
    const pinError = ref('')
    const confirmPinError = ref('')
    const refCode = ref('')
    const refCodeError = ref('')
    const isSubmitting = ref(false)

    const handlePinComplete = (pinValue) => {
      if (pinValue.length === 4) {
        pinError.value = ''
        setTimeout(() => {
          currentStep.value = 2
          confirmPin.value = ''
        }, 300)
      }
    }

    const handleConfirmPinComplete = async (pinValue) => {
      if (pinValue.length === 4) {
        if (pin.value === pinValue) {
          confirmPinError.value = ''
          
          // Проверяем наличие user_id
          const userId = userData.value.id
          if (!userId) {
            confirmPinError.value = 'Unable to get user information'
            return
          }

          // Отправляем PIN на бэкенд
          isSubmitting.value = true
          try {
            const response = await apiService.createPin(userId, pin.value)
            
            if (response.status === 200 || response.status === 201) {
              // PIN успешно создан
              onboardingUtils.savePin(pin.value)
              
              // Переходим на шаг 3 - ввод реферального кода
              refCode.value = ''
              refCodeError.value = ''
              setTimeout(() => {
                currentStep.value = 3
              }, 300)
            } else {
              // Ошибка создания PIN
              confirmPinError.value = 'Failed to create PIN. Please try again.'
              confirmPin.value = ''
            }
          } catch (error) {
            console.error('Error creating PIN:', error)
            confirmPinError.value = 'Failed to create PIN. Please try again.'
            confirmPin.value = ''
          } finally {
            isSubmitting.value = false
          }
        } else {
          confirmPinError.value = 'PIN codes do not match. Please try again.'
          confirmPin.value = ''
        }
      }
    }

    // Обработка нажатия Continue на шаге 3
    const handleContinue = async () => {
      if (!refCode.value || !refCode.value.trim()) {
        refCodeError.value = 'You have entered an incorrect referral code. Please check that you have entered it correctly.'
        return
      }

      // Проверяем наличие user_id
      const userId = userData.value.id
      if (!userId) {
        refCodeError.value = 'You have entered an incorrect referral code. Please check that you have entered it correctly.'
        return
      }

      refCodeError.value = ''
      isSubmitting.value = true

      try {
        const response = await apiService.assignWorker(userId, refCode.value.trim())
        
        if (response.ok || response.status === 200 || response.status === 201) {
          // Реферальный код успешно привязан
          onboardingUtils.completeOnboarding()
          
          // Переходим на экран приветствия
          router.push({ name: 'Welcome' })
        } else {
          // Ошибка привязки реферального кода
          refCodeError.value = 'You have entered an incorrect referral code. Please check that you have entered it correctly.'
        }
      } catch (error) {
        console.error('Error assigning worker:', error)
        // При любой ошибке показываем единое сообщение
        refCodeError.value = 'You have entered an incorrect referral code. Please check that you have entered it correctly.'
      } finally {
        isSubmitting.value = false
      }
    }

    // Если данные загружаются, показываем загрузку
    if (isLoading.value) {
      // Можно добавить индикатор загрузки
    }

    return {
      currentStep,
      pin,
      confirmPin,
      pinError,
      confirmPinError,
      refCode,
      refCodeError,
      isSubmitting,
      handlePinComplete,
      handleConfirmPinComplete,
      handleContinue,
      userData
    }
  }
}
</script>

<style scoped>
.onboarding-container {
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: var(--spacing-lg);
  display: flex;
  align-items: start;
  justify-content: center;
}

.onboarding-content {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.onboarding-header {
  text-align: center;
}

.onboarding-title {
  font-size: var(--font-size-3xl);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-sm) 0;
}

.onboarding-subtitle {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
  margin: 0;
}

.onboarding-step {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.step-indicator {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--color-accent);
  color: var(--color-text-primary);
  border-radius: var(--radius-full);
  font-weight: 600;
  font-size: var(--font-size-base);
}

.step-label {
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  font-weight: 500;
}

.pin-card {
  padding: var(--spacing-xl);
}

.pin-instruction {
  text-align: center;
  margin-bottom: var(--spacing-xl);
}

.pin-title {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-sm) 0;
}

.pin-description {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0;
}

.error-message {
  margin-top: var(--spacing-md);
  padding: var(--spacing-md);
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  text-align: center;
}

.progress-section {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.progress-bar {
  flex: 1;
  height: 4px;
  background: var(--color-bg-secondary);
  border-radius: var(--radius-full);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--color-accent);
  border-radius: var(--radius-full);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: 500;
  min-width: 40px;
}

.referral-card {
  padding: var(--spacing-xl);
}

.referral-instruction {
  text-align: center;
  margin-bottom: var(--spacing-xl);
}

.referral-title {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-sm) 0;
}

.referral-description {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

.referral-input {
  margin-bottom: var(--spacing-lg);
}

.continue-button {
  margin-top: var(--spacing-md);
}
</style>
